import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {Persona} from '../../../brandGuidelines.types';
import {addBrandPersona} from '../../../store/brandPersonas/thunks/addBrandPersona';
import {updateBrandPersona} from '../../../store/brandPersonas/thunks/updateBrandPersona';
import {BrandGuidelinesAgentToolName} from '../brandGuidelinesAgent.enums';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';

enum PersonaAction {
  Add = 'add',
  Update = 'update',
}

const inputSchema = z.object({
  action: z.nativeEnum(PersonaAction).describe('Action to perform: add or update'),
  personaId: z.number().nullable().optional().describe('required for update action only'),
  personaName: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  keyInterests: z.array(z.string()).nullable().optional(),
  painPoints: z.array(z.string()).nullable().optional().describe('Array of pain points'),
  resonatingMessages: z
    .array(z.string())
    .nullable()
    .optional()
    .describe('Array of resonating messages'),
  avoidances: z.array(z.string()).nullable().optional().describe('Array of avoidances'),
  minAge: z.number().nullable().optional().describe('Minimum age (null for no minimum)'),
  maxAge: z.number().nullable().optional().describe('Maximum age (null for no maximum)'),
});

function addOrUpdateBrandPersonaTool(store: CLStore) {
  async function execute({
    input: {action, personaId, ...personaData},
  }: ToolExecuteParams<typeof inputSchema>) {
    if (action === PersonaAction.Update) {
      // Update logic
      if (!personaId) {
        return toolResult(`Persona ID is required for update action.`);
      }

      const brandPersonasData = store.getState().brandGuidelinesPage.brandPersonas.data;

      if (!brandPersonasData) {
        return toolResult(`No personas data available. The personas may not be loaded yet.`);
      }

      const existingPersona = brandPersonasData.find((p) => p.id === personaId);

      if (!existingPersona) {
        return toolResult(`Persona with ID ${personaId} not found.`);
      }

      // Merge updates with existing persona
      const updatedPersona = {
        ...existingPersona,
        ...(personaData.personaName !== undefined && {name: personaData.personaName}),
        ...(personaData.description !== undefined && {description: personaData.description}),
        ...(personaData.keyInterests !== undefined && {keyInterests: personaData.keyInterests}),
        ...(personaData.painPoints !== undefined && {painPoints: personaData.painPoints}),
        ...(personaData.resonatingMessages !== undefined && {
          resonatingMessages: personaData.resonatingMessages,
        }),
        ...(personaData.avoidances !== undefined && {avoidances: personaData.avoidances}),
        ...(personaData.minAge !== undefined && {minAge: personaData.minAge}),
        ...(personaData.maxAge !== undefined && {maxAge: personaData.maxAge}),
      };

      try {
        await store.dispatch(updateBrandPersona(updatedPersona)).unwrap();

        return toolResult(
          `Successfully updated persona ID ${personaId}: ${updatedPersona.name || 'Unnamed'}`,
        );
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to update persona';

        return toolResult(`Failed to update persona ID ${personaId}: ${errorMessage}`);
      }
    } else {
      // Add logic
      const newPersonaData: Omit<Persona, 'id'> = {
        name: personaData.personaName || null,
        avatarUrl: null,
        description: personaData.description || null,
        keyInterests: personaData.keyInterests || [],
        painPoints: personaData.painPoints || [],
        resonatingMessages: personaData.resonatingMessages || [],
        avoidances: personaData.avoidances || [],
        minAge: personaData.minAge || null,
        maxAge: personaData.maxAge || null,
      };

      try {
        await store.dispatch(addBrandPersona(newPersonaData)).unwrap();

        return toolResult(`Successfully added new persona: ${newPersonaData.name || 'Unnamed'}`);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to add persona';

        return toolResult(`Failed to add new persona: ${errorMessage}`);
      }
    }
  }

  return tool({
    name: BrandGuidelinesAgentToolName.AddOrUpdateBrandPersona,
    description: 'Add a new brand persona or update an existing one by ID',
    displayName: 'Adding or updating brand persona',
    inputSchema,
    execute,
  });
}

export default addOrUpdateBrandPersonaTool;
